// Replenishment > Catalogação omnicanal/online — cliente da API interna (sessão).
import axios from 'axios';

const BASE_URL = '/api/replenishment/catalog';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  withCredentials: true,
});

// Sessão expirada -> login (mesmo comportamento do resto da app)
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export type Classificacao =
  | 'excluido_volumoso' | 'downgrade' | 'sem_stock_t'
  | 'omnicanal_core' | 'omnicanal' | 'online_com_levantamento' | 'lancamento' | 'online';
export type Perfil = 'loja' | 'misto' | 'online' | 'pouca_procura';
export type Versao = 'v1' | 'v2' | 'v3';
export type CodV3 = 'D1' | 'D2';
export type Accao =
  | 'passar_omnicanal' | 'completar_lojas' | 'manter' | 'retirar_lojas' | 'downgrade' | 'sem_accao';
export type Situacao = 'online_estimado' | 'omnicanal_parcial' | 'omnicanal_completo';

export interface CatalogStore {
  loja: string;
  stock: number;
  procura_levant: number;
  procura_balcao: number;
  falhas: number | null;
}

export interface CatalogRow {
  snapshot_date: string;
  sku: string;
  nome: string | null;
  cat1: string | null;
  cat2: string | null;
  cat3: string | null;
  estado: string | null;
  online_catalogo: boolean;
  pedidos_levant: number;
  pedidos_envio: number;
  pedidos_online: number;
  pedidos_balcao: number;
  unidades_online: number;
  preco_medio: number | null;
  lojas_com_stock: number;
  stock_lojas_total: number;
  stock_T: number;
  compra_em_curso_T: number;
  unidades_envio_7d: number | null;
  unidades_envio_28d: number | null;
  vel_T_7d: number | null;
  vel_T_28d: number | null;
  /** dias até o T ficar sem stock; null = sem vendas (∞) ou snapshot antigo sem o cálculo */
  duoos_T: number | null;
  vel_total_28d: number | null;
  duoos_T_total: number | null;
  falhas_levant: number | null;
  falhas_catalogacao: number | null;
  falhas_distribuicao: number | null;
  falhas_quantidade: number | null;
  falhas_ruptura: number | null;
  lojas: CatalogStore[];
  classificacao: Classificacao;
  situacao_actual: Situacao;
  accao: Accao;
  score: number;
  regra_disparada: string;
  lojas_em_falta: string[];
  // perfil de canal (28d, em encomendas) e regras v2 — null em snapshots antigos
  procura_loja: number | null;
  procura_total: number | null;
  pct_loja: number | null;
  perfil_canal: Perfil | null;
  classificacao_v1: Classificacao;
  classificacao_v2: Classificacao | null;
  accao_v1: Accao;
  accao_v2: Accao | null;
  regra_disparada_v1: string;
  regra_disparada_v2: string | null;
  score_v1: number;
  score_v2: number | null;
  lojas_em_falta_v1: string[];
  lojas_em_falta_v2: string[] | null;
  lojas_alvo_v2: string[] | null;
  lojas_a_retirar_v2: string[] | null;
  // regra v3 (D1 online / D2 omnicanal) — null em snapshots anteriores à v3
  transito_T?: number | null;
  n_lojas_procura_v3?: number | null;
  procura_loja_v3?: number | null;
  lojas_procura_v3?: string[] | null;
  canal_v3?: 'omnicanal' | 'online' | null;
  cod_distribuicao_v3?: CodV3 | null;
  motivo_v3?: string | null;
  ressalva_v3?: string | null;
  precisa_restock_v3?: boolean | null;
  t_protegido_v3?: boolean | null;
}

export interface CatalogCompareRow {
  cls_v1: Classificacao;
  cls_v2: Classificacao;
  acc_v1: Accao;
  acc_v2: Accao;
  n: number;
}

export interface CatalogListResponse {
  snapshot: string | null;
  count: number;
  limit: number;
  offset: number;
  results: CatalogRow[];
  counts_classificacao: Record<string, number>;
  counts_accao: Record<string, number>;
  /** v3: contagens D1/D2 e por motivo (respeitam os filtros menos cod/motivo) */
  counts_cod?: Record<string, number>;
  counts_motivo?: Record<string, number>;
  counts_restock?: number;
  counts_t_protegido?: number;
  calculated_at: string | null;
}

export interface CatalogRuleResult {
  regra: string;
  passou: boolean;
  valor: string;
  limiar: string;
  descricao: string;
}

export interface CatalogParams {
  window_days: number;
  universe_days: number;
  core_online_orders: number;
  core_pickups: number;
  omni_online_orders: number;
  omni_pickup_failures: number;
  launch_max_age_days: number;
  launch_min_price: number;
  launch_categories: string[];
  bulky_categories: string[];
  bulky_tv_min_inches: number;
  downgrade_states: string[];
  stores: string[];
  t_protection_enabled: boolean;
  t_min_duoos_days: number;
  profile_min_orders: number;
  profile_store_min: number;
  profile_mixed_min: number;
  v2_core_store_orders: number;
  v2_omni_store_orders: number;
  v2_omni_pickup_failures: number;
  v2_remove_window_days: number;
  v2_remove_min_stock: number;
  v2_store_min_orders: number;
  v2_online_pickup_min: number;
  v2_online_pickup_min_duoos: number;
  v3_window_days: number;
  v3_min_stores: number;
}

export interface FailedOrder {
  doc: string;
  loja: string;
  order_date: string;
  order_ts: string | null;
  external_ref: string | null;
  qty: number;
  stock_loja: number;
  stock_t: number;
  causa: string;
}

export interface CatalogHistoryRow {
  snapshot_date: string;
  classificacao: Classificacao;
  situacao_actual: Situacao;
  accao: Accao;
  regra_disparada: string;
  cod_distribuicao_v3?: CodV3 | null;
  motivo_v3?: string | null;
  n_lojas_procura_v3?: number | null;
  precisa_restock_v3?: boolean | null;
  pedidos_online: number;
  lojas_com_stock: number;
}

export interface CatalogDetail extends Omit<CatalogRow, 'lojas'> {
  lojas: CatalogStore[];
  regras: CatalogRuleResult[];
  regras_v2: CatalogRuleResult[] | null;
  motivo_v3_texto?: string | null;
  procura_loja_84d_por_loja?: { loja: string; procura: number }[] | null;
  /** levantamentos + balcão por loja na janela de 90 dias (null em snapshots antigos) */
  procura_loja_90d_por_loja: { loja: string; procura: number }[] | null;
  parametros: Partial<CatalogParams>;
  transito_T: number;
  dias_desde_criacao: number | null;
  criado_em: string | null;
  janela_dias: number;
  catalogacao_sap: string | null;
  calculated_at: string | null;
  historico: CatalogHistoryRow[];
  encomendas_falhadas: FailedOrder[];
}

export interface CatalogMeta {
  snapshots: { snapshot_date: string; skus: number; calculated_at: string | null }[];
  classificacoes: Classificacao[];
  perfis: Perfil[];
  versoes: Versao[];
  accoes: Accao[];
  situacoes: Situacao[];
  cods_v3?: CodV3[];
  motivos_v3?: Record<string, string>;
  cat1: string[];
  estados: string[];
  parametros: Partial<CatalogParams>;
}

export interface CatalogQuery {
  snapshot?: string;
  classificacao?: string[];
  accao?: string[];
  situacao?: string[];
  cat1?: string;
  estado?: string;
  search?: string;
  /** só artigos com DUOOS T < X dias */
  duoos_max?: number;
  versao?: Versao;
  perfil?: string[];
  /** fracções 0..1 */
  pct_loja_min?: number;
  pct_loja_max?: number;
  cls_v1?: string;
  cls_v2?: string;
  acc_v1?: string;
  acc_v2?: string;
  mudou?: boolean;
  /** v3 */
  cod?: string[];
  motivo?: string[];
  restock?: boolean;
  t_protegido?: boolean;
  ordering?: string;
  limit?: number;
  offset?: number;
}

const toParams = (q: CatalogQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  (['snapshot', 'cat1', 'estado', 'search', 'ordering', 'versao', 'cls_v1', 'cls_v2', 'acc_v1', 'acc_v2'] as const).forEach((k) => {
    if (q[k]) params[k] = q[k] as string;
  });
  (['classificacao', 'accao', 'situacao', 'perfil', 'cod', 'motivo'] as const).forEach((k) => {
    if (q[k] && q[k]!.length) params[k] = q[k]!.join(',');
  });
  if (q.duoos_max !== undefined) params.duoos_max = q.duoos_max;
  if (q.pct_loja_min !== undefined) params.pct_loja_min = q.pct_loja_min;
  if (q.pct_loja_max !== undefined) params.pct_loja_max = q.pct_loja_max;
  if (q.mudou) params.mudou = 1;
  if (q.restock) params.restock = 1;
  if (q.t_protegido) params.t_protegido = 1;
  if (q.limit !== undefined) params.limit = q.limit;
  if (q.offset !== undefined) params.offset = q.offset;
  return params;
};

export const replenishmentCatalogApi = {
  list: async (q: CatalogQuery): Promise<CatalogListResponse> =>
    (await client.get('/', { params: toParams(q) })).data,

  detail: async (sku: string, snapshot?: string): Promise<CatalogDetail> =>
    (await client.get(`/${encodeURIComponent(sku)}/`, { params: snapshot ? { snapshot } : {} })).data,

  compare: async (q: CatalogQuery): Promise<{ snapshot: string | null; rows: CatalogCompareRow[] }> =>
    (await client.get('/compare/', { params: toParams(q) })).data,

  meta: async (): Promise<CatalogMeta> => (await client.get('/meta/')).data,

  /** URL do CSV (`;`) — o browser envia o cookie de sessão (mesma origem). */
  exportUrl: (q: CatalogQuery): string => {
    const { limit, offset, ...rest } = q;
    const qs = new URLSearchParams(
      Object.entries(toParams(rest)).map(([k, v]) => [k, String(v)])
    ).toString();
    return `${BASE_URL}/export/${qs ? `?${qs}` : ''}`;
  },
};
